import type { TPaymentMethods } from "@features/checkout/types";
import type { PaymentIcon } from "../types";
import { getLocalPaymentIconUrl } from "./localPaymentIcons";

// For now all methods from paymentMethodResponse are from Adyen.
// If more are added we need to resolve the correct icon for each one.
export const generateAdyenLogoLink = (paymentMethodType: string, locale: string) => {
    // Adyen names these logos with the country, e.g. ebanking_FI.svg. Without it the logo 404s.
    const typesWithSuffixPreserved = ["onlineBanking", "ebanking"];

    const [, countryCode] = locale.split("-");
    const shouldPreserveSuffix = typesWithSuffixPreserved.some((type) =>
        paymentMethodType.includes(type)
    );

    let logoType = paymentMethodType;
    if (!shouldPreserveSuffix && paymentMethodType.endsWith(`_${countryCode}`)) {
        logoType = paymentMethodType.slice(0, -`_${countryCode}`.length);
    }

    return `https://checkoutshopper-live.cdn.adyen.com/checkoutshopper/images/logos/${logoType}.svg`;
};

/**
 * Checkout v2 shows the design's own logos and only falls back to Adyen's for a method it has
 * no file for. Every other caller keeps Adyen's for all of them.
 */
export const mapPaymentMethodResponseToIcons = (
    paymentMethodsResponse: TPaymentMethods[],
    locale: string,
    { preferLocalIcons = false }: { preferLocalIcons?: boolean } = {}
): PaymentIcon[] => {
    const resolveUrl = (type: string) =>
        (preferLocalIcons ? getLocalPaymentIconUrl(type) : undefined) ??
        generateAdyenLogoLink(type, locale);

    const icons: PaymentIcon[] = [];

    if (!Array.isArray(paymentMethodsResponse)) {
        return icons;
    }
    const uniqueMethods = getUniqueMethods(paymentMethodsResponse);

    uniqueMethods?.forEach((paymentMethod) => {
        if (paymentMethod.type === "scheme") {
            paymentMethod?.brands?.forEach((brand) => {
                icons.push({
                    url: resolveUrl(brand),
                    type: brand,
                    name: `${paymentMethod.name} ${brand}`,
                });
            });
            return;
        }
        icons.push({
            url: resolveUrl(paymentMethod.type),
            type: paymentMethod.type,
            name: paymentMethod.name,
        });
    });
    return icons;
};

const getBaseType = (type: string): string => {
    const groupedTypes = {
        klarna: ["klarna", "klarna_account", "klarna_paynow"],
    };

    for (const base in groupedTypes) {
        if (groupedTypes[base].includes(type)) {
            return base;
        }
    }
    return type;
};

const getUniqueMethods = (methods: TPaymentMethods[]): TPaymentMethods[] => {
    const seen = new Set<string>();
    const result: TPaymentMethods[] = [];

    for (const method of methods) {
        const baseType = getBaseType(method.type);
        if (method.type === "scheme" || !seen.has(baseType)) {
            seen.add(baseType);
            result.push(method);
        }
    }

    return result;
};
